import { useState } from "react";
import { FileDown, FileText, Sparkles, Mail, Loader2, Send } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../lib/auth";
import { downloadDiscussionReportPdf, shareReportWithClinic } from "../lib/api";

const INCLUDES = [
  "Your medication details",
  "Every product checked, with its verdict",
  "Interaction details, each linked to its source",
  "Questions to ask your prescriber",
  "Verified alternatives when available",
];

export default function DiscussionReportCard({ result, onPaywall }) {
  const { user } = useAuth();
  const isPro = !!user?.is_pro;
  const [shareOpen, setShareOpen] = useState(false);
  const [clinicEmail, setClinicEmail] = useState("");
  const [note, setNote] = useState("");
  const [sharing, setSharing] = useState(false);
  const [shared, setShared] = useState(false);

  const handleShare = async () => {
    if (!isPro) { onPaywall?.(); return; }
    if (!clinicEmail.trim() || !clinicEmail.includes("@")) {
      toast.error("Enter your clinic's email address.");
      return;
    }
    setSharing(true);
    try {
      const res = await shareReportWithClinic(result, clinicEmail.trim(), note.trim());
      setShared(true);
      toast.success(res.emailed
        ? "Sent to your clinic."
        : "Queued — it will reach your clinic from hello@blendsafe.com.");
    } catch (e) {
      if (e?.response?.status === 402) { onPaywall?.(); return; }
      toast.error(e?.response?.data?.detail || "Couldn't send — try again.");
    } finally { setSharing(false); }
  };

  const handleDownload = async () => {
    // Free + anonymous users: open paywall instead of hitting the backend.
    if (!isPro) {
      onPaywall?.();
      return;
    }
    try {
      const blob = await downloadDiscussionReportPdf(result);
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "blendsafe-discussion-report.pdf";
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("Discussion report downloaded.");
    } catch (e) {
      // Defensive: if the server returns 402 (e.g. Pro just expired) fall
      // through to the paywall instead of showing a generic error.
      if (e?.response?.status === 402) {
        onPaywall?.();
        return;
      }
      toast.error("Couldn't generate the PDF — try again.");
    }
  };

  return (
    <section
      data-testid="discussion-report-card"
      className="relative rounded-3xl bg-white border border-surface-line p-7 sm:p-9 overflow-hidden"
    >
      {/* PRO badge — the PDF discussion report is the Pro-gated feature. */}
      <span
        data-testid="discussion-report-pro-badge"
        className="absolute top-5 right-5 inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-gradient-to-r from-peach-400 to-peach-500 text-white text-[10.5px] font-bold uppercase tracking-wide shadow-sm"
      >
        <Sparkles className="w-3 h-3" strokeWidth={2.5} />
        Pro
      </span>

      <div className="flex items-start gap-4">
        <span className="inline-flex items-center justify-center w-12 h-12 rounded-2xl bg-cream-100 border border-surface-line text-ink shrink-0">
          <FileText className="w-5 h-5" strokeWidth={1.6} />
        </span>
        <div className="flex-1">
          <h3 className="text-xl sm:text-2xl font-extrabold tracking-[-0.02em] text-ink">
            Discussion Report
          </h3>
          <p className="mt-2 text-[14px] text-ink-soft leading-relaxed max-w-xl">
            Take this report to your next appointment. Lists your medication,
            products checked, interactions found, and questions for your prescriber.
          </p>
        </div>
      </div>

      <div className="mt-5 pt-5 border-t border-surface-line">
        <p className="text-[10.5px] uppercase tracking-[0.2em] text-ink-mute font-semibold mb-3">
          Includes
        </p>
        <ul className="grid sm:grid-cols-2 gap-x-6 gap-y-1.5">
          {INCLUDES.map((line) => (
            <li key={line} className="flex items-start gap-2 text-[13px] text-ink-soft">
              <span className="text-safe-deep mt-0.5">✓</span>
              <span>{line}</span>
            </li>
          ))}
        </ul>
      </div>

      <div className="mt-6 flex flex-wrap items-center gap-3">
        <button
          type="button"
          data-testid="discussion-report-download"
          onClick={handleDownload}
          className="inline-flex items-center justify-center gap-2 h-12 px-5 rounded-2xl bg-ink hover:bg-ink-soft text-cream-50 text-[14px] font-bold transition-all hover:scale-[1.02] active:scale-[0.98]"
        >
          <FileDown className="w-4 h-4" />
          Download Report (PDF)
        </button>
        <button
          type="button"
          data-testid="share-derm-open"
          onClick={() => (isPro ? setShareOpen((v) => !v) : onPaywall?.())}
          className="inline-flex items-center justify-center gap-2 h-12 px-5 rounded-2xl bg-white border border-surface-line hover:border-ink text-ink text-[14px] font-bold transition-all active:scale-[0.98]"
        >
          <Mail className="w-4 h-4" />
          Email this to my clinic
        </button>
      </div>

      {shareOpen && isPro && (
        <div data-testid="share-derm-form" className="mt-4 rounded-2xl border border-surface-line bg-cream-100/50 p-5">
          {shared ? (
            <p data-testid="share-derm-done" className="text-[14px] text-ink-soft flex items-center gap-2">
              <Send className="w-4 h-4 text-safe-deep" />
              Your Discussion Report is on its way to {clinicEmail} from hello@blendsafe.com.
            </p>
          ) : (
            <>
              <label className="text-[11px] uppercase tracking-wide font-bold text-ink-mute">Your clinic's email</label>
              <input
                data-testid="share-derm-email"
                type="email"
                value={clinicEmail}
                onChange={(e) => setClinicEmail(e.target.value)}
                placeholder="frontdesk@yourclinic.com"
                className="mt-1 w-full h-11 px-3 rounded-lg border border-surface-line bg-white text-sm text-ink"
              />
              <textarea
                data-testid="share-derm-note"
                value={note}
                onChange={(e) => setNote(e.target.value)}
                rows={2}
                placeholder="Optional note for your clinician…"
                className="mt-2 w-full px-3 py-2 rounded-lg border border-surface-line bg-white text-sm text-ink"
              />
              <div className="mt-3 flex items-center gap-3">
                <button
                  data-testid="share-derm-send"
                  onClick={handleShare}
                  disabled={sharing}
                  className="inline-flex items-center gap-2 h-10 px-4 rounded-xl bg-ink hover:bg-ink-soft text-cream-50 text-[13px] font-bold disabled:opacity-60"
                >
                  {sharing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                  Send to clinic
                </button>
                <button className="text-[13px] text-ink-mute hover:text-ink" onClick={() => setShareOpen(false)}>Cancel</button>
              </div>
            </>
          )}
        </div>
      )}
    </section>
  );
}
